import React, {useCallback, useEffect, useState} from 'react';
import EditorLayout from '../../../uiToolkit/Editor/EditorLayout';
import ReactFlow, {
  addEdge,
  useEdgesState,
  useNodesState,
  Controls,
  Background,
} from 'reactflow';
import EditorHeader from '../../../uiToolkit/Editor/EditorHeader';
import EditorOptions from '../../../uiToolkit/Editor/EditorOptions';
import {lessonEditorText} from '../lessonEditorText';
import {VideoSourceOptions} from '../options/VideoSourceOptions';
import {buildNodeTree} from './nodeTreeBuilder';
import {LessonContentList} from '../../../data/models/LessonContent/LessonContentList';
import {
  ACKNOWLEDGMENT_NODE,
  BRANCH_BACKGROUND_FREEFORM,
  BRANCH_IMAGE_FREEFORM,
  BRANCH_SHAPE_FREEFORM,
  BRANCH_TEXT_FREEFORM,
  CHALLENGE_BRANCH_AI,
  CHALLENGE_BRANCH_HEADER,
  CHALLENGE_BRANCH_IMAGE_OPTION,
  CHALLENGE_BRANCH_QUESTION,
  CHALLENGE_BRANCH_TEXT_OPTION,
  CHOICE_HEADER_NODE_TYPE,
  CHOICE_IMAGE_OPTION,
  CHOICE_TEXT_OPTION,
  CHOICE_TEXT_QUESTION,
  IMAGE_NODE_TYPE,
  JUMP_TO_NODE,
  LessonContentBase,
  MULTIPLE_CHOICE,
  OPENENDED_QUESTION_NODE_TYPE,
  OVERLAY_VIDEO_TYPE,
  OVERLAY_VIMEO_VIDEO_TYPE,
  VIDEO_NODE_TYPE,
  VIMEO_VIDEO_TYPE,
} from '../../../data/models/LessonContent/LessonContentBase';
import {ChoiceQuestionOptions} from '../options/ChoiceQuestionOptions';
import {ChoiceTextOptionOptions} from '../options/ChoiceTextOptionOptions';
import {ChoiceImageOptionOptions} from '../options/ChoiceImageOptionOptions';
import {ChoiceHeaderOptions} from '../options/ChoiceHeaderOptions';
import {MChoiceOptions} from '../options/MChoiceOptions';
import './styles.css';
import 'reactflow/dist/style.css';
import {ChallengeQuestionOptions} from '../options/ChallengeQuestionOptions';
import {ChallengeTextOptionOptions} from '../options/ChallengeTextOptionOptions';
import {ChallengeImageOptionOptions} from '../options/ChallengeImageOptionOptions';
import ChallengeBranchAIOptionsContainer from '../options/ChallengeBranchAIOptions/container/ChallengeBranchAIOptionsContainer';
import {VimeoVideoSourceOptions} from '../options/VimeoVideoSourceOptions';
import {VideoOverlayOptions} from '../options/VideoOverlayOptions';
import {FreeformImageOptions} from '../options/FreeformImageOptions';
import {FreeformTextOptions} from '../options/FreeformTextOptions';
import {FreeformShapeOptions} from '../options/FreeformShapeOptions';
import {JumpToOptions} from '../options/JumpToOptions';
import {VideoBackgorundOptions} from '../options/ChoiceVideoBackgroundOptions';
import {AcknowledgmentOptions} from '../options/AcknowledgmentOptions';
import {getLayoutedElements} from '../../../modules/EditorSettings/layoutSettings';
import {edgeTypes, nodeTypes} from '../config';
import {ImageSourceOptions} from '../options/ImageSourceOptions';
import {SidebarNavigation} from '../../../modules/Editor/SidebarNavigation';
import {OpenEndedQuestionOptions} from '../options/OpenEndedQuestionOptions';

const OPTIONS_TAB = 'Options';

interface Props {
  onBack: () => void;
  selectedItem?: LessonContentBase;
  selectedItemType?: number;
  lessonContent?: LessonContentList;
  onClearSelectedNode: () => void;
  onChangesMade: () => void;
  onPreview: () => void;
  onPublish?: () => void;
  lessonIsPublisehd?: boolean;
  breadcrumbs: {title: string; link?: string}[];
  onExportScorm?: () => void;
  hideScorm: boolean;
  isGradable: boolean;
  lessonTitle: string;
  hideSidebar?: boolean;
}

const CustomEditorUI = React.memo(
  ({
    onBack,
    selectedItem,
    selectedItemType,
    lessonContent,
    onClearSelectedNode,
    onChangesMade,
    onPreview,
    onPublish,
    lessonIsPublisehd,
    breadcrumbs,
    onExportScorm,
    hideScorm,
    isGradable,
    lessonTitle,
    hideSidebar,
  }: Props) => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [showSidebar, setShowSidebar] = useState(true);

    useEffect(() => {
      if (lessonContent) {
        setNodes([]);
        setEdges([]);
        const {nodes, edges} = buildNodeTree(lessonTitle, lessonContent, false);
        const {nodes: layoutedNodes, edges: layoutedEdges} =
          getLayoutedElements(nodes, edges);
        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
      }
    }, [lessonContent, lessonTitle, lessonIsPublisehd]);

    const onConnect = useCallback(
      (params: any) => setEdges(eds => addEdge(params, eds)),
      [setEdges],
    );

    return (
      <div className="EditorRoot">
        <EditorLayout
          isPublished={lessonIsPublisehd}
          onContentClick={onClearSelectedNode}
          showSidebar={showSidebar}
          sidebarSet={() => setShowSidebar(!showSidebar)}
          header={
            <EditorHeader
              breadcrumbs={breadcrumbs}
              onBack={onBack}
              nextTitle={lessonEditorText.preview}
              onNext={onPreview}
              onPublish={onPublish}
              onExportScorm={onExportScorm}
              canExportScorm={lessonIsPublisehd || false}
              hideScorm={hideScorm}
            />
          }
          sideBar={!hideSidebar ? <SidebarNavigation /> : undefined}
          content={
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              fitView={lessonContent?.contentList.rootId !== null}
              defaultViewport={
                lessonContent?.contentList.rootId === null
                  ? {
                      x: window.innerWidth / 3,
                      y: 40,
                      zoom: 0.8,
                    }
                  : undefined
              }>
              <Controls showInteractive={false} />
              <Background />
            </ReactFlow>
          }
          options={
            <EditorOptions
              tabs={[{tabName: OPTIONS_TAB}]}
              entityToEdit={selectedItem}
              entityType={selectedItemType?.toString()}
              emptyStateText={lessonEditorText.emptyState}>
              <VideoSourceOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={VIDEO_NODE_TYPE.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                lessonContent={lessonContent}
                isGradable={isGradable}
              />
              <VideoOverlayOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={OVERLAY_VIDEO_TYPE.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                lessonContent={lessonContent}
                isGradable={isGradable}
                rootId={lessonContent?.contentList.rootId || ''}
              />
              <VimeoVideoSourceOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={VIMEO_VIDEO_TYPE.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                lessonContent={lessonContent}
                isGradable={isGradable}
              />
              <VideoOverlayOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={OVERLAY_VIMEO_VIDEO_TYPE.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                lessonContent={lessonContent}
                isGradable={isGradable}
                rootId={lessonContent?.contentList.rootId || ''}
              />
              <ChoiceQuestionOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={CHOICE_TEXT_QUESTION.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                isGradable={isGradable}
              />
              <ChoiceTextOptionOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={CHOICE_TEXT_OPTION.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                isGradable={isGradable}
              />
              <ChoiceImageOptionOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={CHOICE_IMAGE_OPTION.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                isGradable={isGradable}
              />
              <ChoiceHeaderOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={CHOICE_HEADER_NODE_TYPE.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                isGradable={isGradable}
                isChallenge={false}
              />
              <ChoiceHeaderOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={CHALLENGE_BRANCH_HEADER.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                isChallenge
                rootId={lessonContent?.contentList.rootId || ''}
                isGradable={isGradable}
              />
              <MChoiceOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={MULTIPLE_CHOICE.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                isGradable={isGradable}
              />
              <ChallengeQuestionOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={CHALLENGE_BRANCH_QUESTION.toString()}
                isGradable={isGradable}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
              />
              <ChallengeTextOptionOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={CHALLENGE_BRANCH_TEXT_OPTION.toString()}
                isGradable={isGradable}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
              />
              <ChallengeImageOptionOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={CHALLENGE_BRANCH_IMAGE_OPTION.toString()}
                isGradable={isGradable}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                lessonContent={lessonContent}
              />
              <ChallengeBranchAIOptionsContainer
                contentForTab={OPTIONS_TAB}
                optionsForType={CHALLENGE_BRANCH_AI.toString()}
                isGradable={isGradable}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                lessonContent={lessonContent}
              />
              <FreeformImageOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={BRANCH_IMAGE_FREEFORM.toString()}
                onChangesMade={onChangesMade}
                isGradable={isGradable}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
              />
              <FreeformShapeOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={BRANCH_SHAPE_FREEFORM.toString()}
                onChangesMade={onChangesMade}
                isGradable={isGradable}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
              />
              <FreeformTextOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={BRANCH_TEXT_FREEFORM.toString()}
                isGradable={isGradable}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
              />
              <JumpToOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={JUMP_TO_NODE.toString()}
                onChangesMade={onChangesMade}
                isGradable={isGradable}
                isPublished={lessonIsPublisehd}
                lessonContent={lessonContent}
                rootId={lessonContent?.contentList.rootId || ''}
              />
              <VideoBackgorundOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={BRANCH_BACKGROUND_FREEFORM.toString()}
                onChangesMade={onChangesMade}
                isGradable={isGradable}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
              />
              <AcknowledgmentOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={ACKNOWLEDGMENT_NODE.toString()}
                onChangesMade={onChangesMade}
                isGradable={isGradable}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
              />
              <ImageSourceOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={IMAGE_NODE_TYPE.toString()}
                onChangesMade={onChangesMade}
                isGradable={isGradable}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                lessonContent={lessonContent}
              />
              <OpenEndedQuestionOptions
                contentForTab={OPTIONS_TAB}
                optionsForType={OPENENDED_QUESTION_NODE_TYPE.toString()}
                onChangesMade={onChangesMade}
                isPublished={lessonIsPublisehd}
                rootId={lessonContent?.contentList.rootId || ''}
                isGradable={isGradable}
              />
            </EditorOptions>
          }
          customContent
        />
      </div>
    );
  },
);

export default CustomEditorUI;
